import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";
import Icon from "@mui/material/Icon";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { formatLocalDate, formatLocalTime } from "utils/dateFormatter";

interface InterstitialAdCardProps {
    ad: any;
    onClick?: (ad: any) => void;
    onEdit?: (ad: any) => void;
    onDelete?: () => void;
    darkMode?: boolean;
}

function InterstitialAdCard({ ad, onClick, onEdit, onDelete, darkMode }: InterstitialAdCardProps) {
    const getStatusColor = (isActive: boolean) => {
        return isActive ? "success" : "error";
    };

    const getStatusLabel = (isActive: boolean) => {
        return isActive ? "활성" : "비활성";
    };

    return (
        <Card
            sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                transition: "all 0.2s ease-in-out",
                cursor: onClick ? "pointer" : "default",
                border: darkMode ? "1px solid rgba(255, 255, 255, 0.12)" : "1px solid rgba(0, 0, 0, 0.12)",
                boxShadow: darkMode
                    ? "0 2px 8px rgba(0, 0, 0, 0.3)"
                    : "0 2px 8px rgba(0, 0, 0, 0.08)",
                "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: darkMode
                        ? "0 8px 20px rgba(255, 255, 255, 0.15)"
                        : "0 8px 20px rgba(0, 0, 0, 0.15)",
                    borderColor: darkMode ? "rgba(255, 255, 255, 0.2)" : "rgba(0, 0, 0, 0.2)",
                },
            }}
            onClick={() => onClick && onClick(ad)}
        >
            <MDBox p={3}>
                {/* 제목과 상태 */}
                <MDBox
                    display="flex"
                    justifyContent="space-between"
                    alignItems="flex-start"
                    mb={2}
                >
                    <MDTypography variant="h5" fontWeight="bold">
                        {ad.title}
                    </MDTypography>
                    <Chip
                        label={getStatusLabel(ad.isActive)}
                        color={getStatusColor(ad.isActive)}
                        size="small"
                        sx={{
                            fontWeight: "medium",
                            ...(ad.isActive && {
                                backgroundColor: "#4CAF50",
                                color: "#ffffff",
                                "& .MuiChip-label": {
                                    color: "#ffffff",
                                },
                            }),
                        }}
                    />
                </MDBox>

                {/* 광고 이미지 */}
                {ad.imageUrl && (
                    <MDBox
                        mb={2}
                        sx={{
                            width: "100%",
                            paddingTop: "100%", // 1:1 비율 (정사각형)
                            position: "relative",
                            overflow: "hidden",
                            borderRadius: "8px",
                            backgroundColor: darkMode ? "rgba(255, 255, 255, 0.05)" : "rgba(0, 0, 0, 0.05)",
                        }}
                    >
                        <img
                            src={ad.imageUrl}
                            alt={ad.title}
                            style={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                            }}
                            onError={(e) => {
                                (e.target as HTMLImageElement).style.display = "none";
                            }}
                        />
                    </MDBox>
                )}

                <Divider sx={{ my: 2 }} />

                <MDBox>
                    <MDBox display="flex" alignItems="center" mb={1.5}>
                        <Icon fontSize="small" sx={{ mr: 1.5, color: darkMode ? "rgba(255, 255, 255, 0.7)" : "rgba(0, 0, 0, 0.6)" }}>
                            business
                        </Icon>
                        <MDTypography variant="body2" color="text">
                            {ad.advertiserName}
                        </MDTypography>
                    </MDBox>

                    <MDBox display="flex" alignItems="center" mb={1.5}>
                        <Icon fontSize="small" sx={{ mr: 1.5, color: darkMode ? "rgba(255, 255, 255, 0.7)" : "rgba(0, 0, 0, 0.6)" }}>
                            event
                        </Icon>
                        <MDTypography variant="body2" color="text">
                            {ad.formattedStartDate || ad.startDate} ~ {ad.formattedEndDate || ad.endDate}
                        </MDTypography>
                    </MDBox>

                    <MDBox display="flex" alignItems="center" mb={1.5}>
                        <Icon fontSize="small" sx={{ mr: 1.5, color: darkMode ? "rgba(255, 255, 255, 0.7)" : "rgba(0, 0, 0, 0.6)" }}>
                            analytics
                        </Icon>
                        <MDTypography variant="body2" color="text">
                            조회: {ad.viewCount} / 클릭: {ad.clickCount} / 건너뛰기: {ad.skipCount}
                        </MDTypography>
                    </MDBox>

                    <MDBox display="flex" alignItems="center" justifyContent="space-between">
                        <MDBox display="flex" alignItems="center">
                            <Icon fontSize="small" sx={{ mr: 1.5, color: darkMode ? "rgba(255, 255, 255, 0.5)" : "rgba(0, 0, 0, 0.5)" }}>
                                schedule
                            </Icon>
                            <MDTypography variant="caption" color="text.secondary">
                                등록일:
                                <br />
                                {formatLocalDate(ad.rawCrtDt)} {formatLocalTime(ad.rawCrtDt)}
                            </MDTypography>
                        </MDBox>
                        {(onEdit || onDelete) && (
                            <MDBox display="flex" gap={0.5}>
                                {onEdit && (
                                    <IconButton
                                        size="small"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onEdit(ad);
                                        }}
                                        sx={{
                                            color: darkMode ? "rgba(255, 255, 255, 0.7)" : "rgba(0, 0, 0, 0.6)",
                                            "&:hover": {
                                                backgroundColor: darkMode ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.04)",
                                                color: "#49a3f1",
                                            },
                                        }}
                                    >
                                        <Icon fontSize="small">edit</Icon>
                                    </IconButton>
                                )}
                                {onDelete && (
                                    <IconButton
                                        size="small"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onDelete();
                                        }}
                                        sx={{
                                            color: darkMode ? "rgba(255, 255, 255, 0.7)" : "rgba(0, 0, 0, 0.6)",
                                            "&:hover": {
                                                backgroundColor: darkMode ? "rgba(255, 100, 100, 0.1)" : "rgba(255, 0, 0, 0.04)",
                                                color: "#f44336",
                                            },
                                        }}
                                    >
                                        <Icon fontSize="small">delete</Icon>
                                    </IconButton>
                                )}
                            </MDBox>
                        )}
                    </MDBox>
                </MDBox>
            </MDBox>
        </Card>
    );
}

export default InterstitialAdCard;
